import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Workflow,
  Cpu,
  Database,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  ConfidenceBar,
  KeyValueGrid,
  MetricCard,
  StatusBadge,
  StatusDot,
  Table,
  Tabs,
  type Column,
  type TabItem,
} from '@/components';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
} from 'recharts';
import { useData, useUI } from '@/data/stores';
import { fmtNumber, fmtRelative, fmtUTC, fmtCoord } from '@/lib/format';
import { demoTimestamp, downloadJson } from '@/lib/export';
import { severityColor, severityLabel } from '@/lib/color';
import { runCascade } from '@/domain';
import type { Alert, DependencyEdge, OTDevice } from '@/types';
import './assets.css';

export function AssetDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { assets, alerts, edges, otDevices, vulnerabilities } = useData();
  const notify = useUI((s) => s.notifyAction);
  const asset = assets.find((a) => a.asset_id === id);

  const linkedAlerts = useMemo(
    () => (asset ? alerts.filter((a) => a.asset_ids.includes(asset.asset_id)) : []),
    [alerts, asset],
  );
  const upstream = useMemo(
    () => (asset ? edges.filter((e) => e.to_asset_id === asset.asset_id) : []),
    [edges, asset],
  );
  const downstream = useMemo(
    () => (asset ? edges.filter((e) => e.from_asset_id === asset.asset_id) : []),
    [edges, asset],
  );
  const devices = useMemo(
    () => (asset ? otDevices.filter((d) => d.asset_id === asset.asset_id) : []),
    [otDevices, asset],
  );
  const vulns = useMemo(
    () =>
      asset
        ? vulnerabilities.filter((v) => v.affected_asset_ids.includes(asset.asset_id))
        : [],
    [vulnerabilities, asset],
  );

  const cascadePreview = useMemo(() => {
    if (!asset) return null;
    return runCascade(
      {
        scenario_id: `preview-${asset.asset_id}`,
        name: 'Preview',
        mode: 'failure',
        initial_failed_assets: [asset.asset_id],
        created_at: new Date().toISOString(),
      },
      assets,
      edges,
    );
  }, [asset, assets, edges]);

  if (!asset) {
    return (
      <div className="iris-page">
        <h1>Asset not found</h1>
        <Button onClick={() => navigate('/assets')}>Back to registry</Button>
      </div>
    );
  }

  const sensorSeries = generateSensorSeries(asset.condition_score);

  const tabs: TabItem[] = [
    {
      key: 'overview',
      label: 'Overview',
      content: (
        <div className="iris-detail-grid">
          <Card title="Identity & metadata">
            <KeyValueGrid
              cols={2}
              items={[
                { label: 'Asset ID', value: asset.asset_id, mono: true },
                { label: 'Name', value: asset.name },
                { label: 'Owner / operator', value: asset.owner_operator },
                { label: 'Type', value: asset.type.replace(/_/g, ' ') },
                { label: 'Sector', value: asset.sector.replace(/_/g, ' ') },
                { label: 'Sub-sector', value: asset.sub_sector ?? '-' },
                { label: 'Country', value: asset.country, mono: true },
                { label: 'Region', value: asset.region ?? '-' },
                {
                  label: 'Coordinates',
                  value: fmtCoord(asset.coordinates.lat, asset.coordinates.lon),
                  mono: true,
                },
                { label: 'Tags', value: asset.tags.join(' · ') || '-' },
              ]}
            />
          </Card>
          <Card title="Operational state">
            <KeyValueGrid
              cols={2}
              items={[
                {
                  label: 'Status',
                  value: <StatusBadge status={asset.operational_status} />,
                },
                {
                  label: 'Criticality',
                  value: `${asset.criticality_rating} / 5`,
                  mono: true,
                },
                {
                  label: 'Capacity',
                  value: asset.capacity
                    ? `${fmtNumber(asset.capacity.value)} ${asset.capacity.unit}`
                    : '-',
                  mono: true,
                },
                {
                  label: 'Voltage level',
                  value: asset.voltage_level_kv ? `${asset.voltage_level_kv} kV` : '-',
                  mono: true,
                },
                {
                  label: 'Commissioned',
                  value: asset.commissioning_date ?? '-',
                  mono: true,
                },
                {
                  label: 'Expected life',
                  value: asset.expected_life_years
                    ? `${asset.expected_life_years} years`
                    : '-',
                  mono: true,
                },
                {
                  label: 'Condition',
                  value: <ConfidenceBar value={asset.condition_score} />,
                },
                {
                  label: 'Last telemetry',
                  value: asset.last_telemetry_at ? fmtUTC(asset.last_telemetry_at) : '-',
                  mono: true,
                },
              ]}
            />
          </Card>
          <Card title="Compliance & classification">
            <KeyValueGrid
              cols={2}
              items={[
                {
                  label: 'Classification',
                  value: (
                    <Badge color="var(--status-info)">
                      {asset.classification_level.toUpperCase()}
                    </Badge>
                  ),
                },
                {
                  label: 'Frameworks',
                  value: (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {asset.regulatory_framework.map((f) => (
                        <Badge key={f} color="var(--text-secondary)">
                          {f}
                        </Badge>
                      ))}
                    </div>
                  ),
                },
                {
                  label: 'Insurance tier',
                  value: asset.insurance_tier ?? '-',
                  mono: true,
                },
                {
                  label: 'Parent asset',
                  value: asset.parent_asset_id ?? '-',
                  mono: true,
                },
                {
                  label: 'Data sources',
                  value: asset.data_sources.length ? asset.data_sources.join(', ') : '-',
                  mono: true,
                },
              ]}
            />
          </Card>
          <Card title="Live process telemetry (synthetic)">
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={sensorSeries}>
                  <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="2 2" />
                  <XAxis
                    dataKey="t"
                    stroke="var(--text-tertiary)"
                    fontSize={10}
                    tick={{ fill: 'var(--text-tertiary)' }}
                  />
                  <YAxis
                    stroke="var(--text-tertiary)"
                    fontSize={10}
                    tick={{ fill: 'var(--text-tertiary)' }}
                  />
                  <RTooltip
                    contentStyle={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-primary)',
                      fontSize: 11,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="output"
                    stroke="var(--series-1)"
                    strokeWidth={1.4}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="temp"
                    stroke="var(--series-3)"
                    strokeWidth={1.4}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      ),
    },
    {
      key: 'twin',
      label: 'Digital Twin',
      content: <DigitalTwinPanel asset={asset} />,
    },
    {
      key: 'deps',
      label: 'Dependencies',
      badge: <span>{upstream.length + downstream.length}</span>,
      content: (
        <div className="iris-detail-grid">
          <Card title={`Upstream (${upstream.length})`}>
            <DependencyTable edges={upstream} mode="upstream" />
          </Card>
          <Card title={`Downstream (${downstream.length})`}>
            <DependencyTable edges={downstream} mode="downstream" />
          </Card>
          <Card
            title="Cascade preview"
            subtitle={`If ${asset.name} fails: ${cascadePreview?.total_assets_impacted ?? 0} assets impacted`}
            actions={
              <Button
                variant="primary"
                size="sm"
                icon={<Workflow size={12} strokeWidth={1.5} />}
                onClick={() =>
                  navigate(`/scenarios?seed=${encodeURIComponent(asset.asset_id)}`)
                }
              >
                Open in scenarios
              </Button>
            }
          >
            <CascadePreviewList preview={cascadePreview} />
          </Card>
        </div>
      ),
    },
    {
      key: 'alerts',
      label: 'Alerts',
      badge: <span>{linkedAlerts.length}</span>,
      content: <AssetAlertsTable alerts={linkedAlerts} navigate={navigate} />,
    },
    {
      key: 'ot',
      label: 'OT Inventory',
      badge: <span>{devices.length}</span>,
      content: <DeviceTable devices={devices} />,
    },
    {
      key: 'vulns',
      label: 'Vulnerabilities',
      badge: <span>{vulns.length}</span>,
      content: <VulnerabilityTable vulns={vulns} />,
    },
  ];

  return (
    <div className="iris-page">
      <header className="iris-page-header iris-detail-header">
        <div>
          <button className="iris-back" onClick={() => navigate('/assets')}>
            <ArrowLeft size={14} strokeWidth={1.5} /> All assets
          </button>
          <h1 style={{ marginTop: 4 }}>{asset.name}</h1>
          <p className="caption">
            <span className="mono">{asset.asset_id}</span> · {asset.country} ·{' '}
            {asset.type.replace(/_/g, ' ')} · operated by {asset.owner_operator}
          </p>
        </div>
        <div className="iris-page-actions">
          <StatusDot status={asset.operational_status} size={10} pulsing={asset.operational_status === 'offline'} />
          <StatusBadge status={asset.operational_status} />
          <Button
            icon={<Download size={14} strokeWidth={1.5} />}
            onClick={() => {
              downloadJson(`iris-twin-${asset.asset_id}-${demoTimestamp()}.json`, {
                asset,
                linked_alerts: linkedAlerts,
                upstream_dependencies: upstream,
                downstream_dependencies: downstream,
                ot_inventory: devices,
                vulnerabilities: vulns,
                cascade_preview: cascadePreview,
              });
              notify({
                kind: 'success',
                title: 'Digital twin exported',
                body: `${asset.asset_id} twin, dependencies, alerts, OT inventory, and cascade preview downloaded.`,
              });
            }}
          >
            Export twin
          </Button>
          <Button
            variant="primary"
            icon={<Workflow size={14} strokeWidth={1.5} />}
            onClick={() => navigate(`/scenarios?seed=${encodeURIComponent(asset.asset_id)}`)}
          >
            Run scenario
          </Button>
        </div>
      </header>

      <section className="iris-page-metrics">
        <MetricCard
          label="Criticality"
          value={`${asset.criticality_rating}/5`}
          caption={asset.criticality_rating === 5 ? 'Nationally critical' : ''}
          status={
            asset.criticality_rating === 5
              ? 'var(--status-critical)'
              : asset.criticality_rating === 4
                ? 'var(--status-degraded)'
                : 'var(--status-info)'
          }
        />
        <MetricCard label="Active alerts" value={linkedAlerts.filter((a) => a.status !== 'resolved').length} />
        <MetricCard label="Upstream deps" value={upstream.length} caption="feeders" />
        <MetricCard label="Downstream deps" value={downstream.length} caption="impacted on failure" />
        <MetricCard
          label="Cascade reach"
          value={cascadePreview?.total_assets_impacted ?? 0}
          caption={`if ${asset.name} fails`}
        />
        <MetricCard label="OT inventory" value={devices.length} />
      </section>

      <Tabs items={tabs} />
    </div>
  );
}

function DigitalTwinPanel({ asset }: { asset: NonNullable<ReturnType<typeof useData.getState>['assets'][number]> }) {
  const ontologies =
    asset.sector === 'energy_transmission' || asset.sector === 'energy_distribution'
      ? ['IEC 61850', 'IEC 61968/61970 (CIM)', 'CGMES', 'CityGML+ADE', 'OWL']
      : asset.sector === 'maritime_ports'
        ? ['IHO S-100', 'IALA G1180', 'CityGML+ADE']
        : asset.sector === 'oil_gas'
          ? ['ISO 15926', 'POSC Caesar (RDL)', 'IEC 62443']
          : ['ISO 23247', 'DTDL', 'OWL'];

  return (
    <div className="iris-detail-grid">
      <Card
        title="Semantic ontology"
        subtitle="Knowledge graph encoding for this asset class"
      >
        <ul className="iris-ontology-list">
          {ontologies.map((o) => (
            <li key={o}>
              <Cpu size={12} strokeWidth={1.5} /> <span className="mono">{o}</span>
            </li>
          ))}
        </ul>
      </Card>
      <Card
        title="Inferred remaining useful life"
        subtitle="From condition score, vintage, and sensor trends"
      >
        <div className="iris-rul">
          <span className="iris-rul-value mono">
            {Math.round((asset.condition_score / 100) * (asset.expected_life_years ?? 30))}
          </span>
          <span className="iris-rul-unit">years</span>
        </div>
        <ConfidenceBar
          value={Math.min(100, 50 + asset.condition_score / 3)}
          label="confidence"
        />
      </Card>
      <Card title="Predicted failure modes" subtitle="LSTM + XGBoost ensemble (synthetic)">
        <ul className="iris-failure-modes">
          <li>
            <span>Bearing wear / vibration trend → service required</span>
            <span className="mono">12% / 30d</span>
          </li>
          <li>
            <span>Insulation degradation (DGA trend)</span>
            <span className="mono">4% / 90d</span>
          </li>
          <li>
            <span>Cooling system underperformance</span>
            <span className="mono">2% / 30d</span>
          </li>
        </ul>
      </Card>
      <Card title="Source inventory">
        <ul className="iris-source-list">
          {asset.data_sources.map((s) => (
            <li key={s}>
              <Database size={12} strokeWidth={1.5} /> <span className="mono">{s}</span>
            </li>
          ))}
          {asset.data_sources.length === 0 && <li className="caption">No live sources connected.</li>}
        </ul>
      </Card>
    </div>
  );
}

function DependencyTable({
  edges,
  mode,
}: {
  edges: DependencyEdge[];
  mode: 'upstream' | 'downstream';
}) {
  const cols: Column<DependencyEdge>[] = [
    {
      key: 'asset',
      header: mode === 'upstream' ? 'Source' : 'Target',
      mono: true,
      sortValue: (e) => (mode === 'upstream' ? e.from_asset_id : e.to_asset_id),
      render: (e) => (mode === 'upstream' ? e.from_asset_id : e.to_asset_id),
    },
    {
      key: 'relation',
      header: 'Relation',
      sortValue: (e) => e.relation,
      render: (e) => <Badge color="var(--status-info)">{e.relation.replace(/_/g, ' ')}</Badge>,
    },
    {
      key: 'loss',
      header: 'Loss factor',
      mono: true,
      align: 'right',
      sortValue: (e) => e.loss_factor,
      render: (e) => `${(e.loss_factor * 100).toFixed(0)}%`,
    },
    {
      key: 'prop',
      header: 'Time-to-impact',
      mono: true,
      align: 'right',
      sortValue: (e) => e.propagation_seconds,
      render: (e) =>
        e.propagation_seconds < 60
          ? `${e.propagation_seconds}s`
          : e.propagation_seconds < 3600
            ? `${Math.round(e.propagation_seconds / 60)}m`
            : `${Math.round(e.propagation_seconds / 3600)}h`,
    },
    {
      key: 'backup',
      header: 'Redundant',
      align: 'center',
      sortValue: (e) => Number(e.has_backup),
      render: (e) => (e.has_backup ? '✓' : '-'),
    },
    {
      key: 'confidence',
      header: 'Conf',
      mono: true,
      align: 'right',
      sortValue: (e) => e.confidence,
      render: (e) => `${e.confidence}%`,
    },
  ];
  return (
    <Table
      rows={edges}
      columns={cols}
      rowKey={(e) => e.edge_id}
      density="compact"
      empty="No dependencies modelled."
    />
  );
}

function CascadePreviewList({
  preview,
}: {
  preview: ReturnType<typeof runCascade> | null;
}) {
  if (!preview || preview.total_assets_impacted <= 1) {
    return (
      <div className="caption" style={{ padding: 8 }}>
        No cascade beyond the seed asset.
      </div>
    );
  }
  return (
    <ul className="iris-cascade-list">
      {preview.affected.slice(1, 8).map((n) => (
        <li key={n.asset_id}>
          <span className="mono">{n.asset_id}</span>
          <span className="caption">{n.reason}</span>
          <span
            className="iris-cascade-badge"
            style={{
              color: severityColor(n.severity),
              background: `${severityColor(n.severity)}26`,
            }}
          >
            {severityLabel(n.severity)}
          </span>
        </li>
      ))}
      {preview.total_assets_impacted > 8 && (
        <li className="caption">
          + {preview.total_assets_impacted - 8} more affected assets
        </li>
      )}
    </ul>
  );
}

function AssetAlertsTable({
  alerts,
  navigate,
}: {
  alerts: Alert[];
  navigate: (path: string) => void;
}) {
  const cols: Column<Alert>[] = [
    {
      key: 'severity',
      header: 'Sev',
      width: '6%',
      sortValue: (a) => -a.severity,
      render: (a) => (
        <span
          style={{
            color: severityColor(a.severity),
            fontWeight: 600,
            fontSize: 11,
            textTransform: 'uppercase',
          }}
        >
          ● {severityLabel(a.severity)}
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Alert',
      width: '50%',
      sortValue: (a) => a.title,
      render: (a) => (
        <div>
          <div>{a.title}</div>
          <div className="caption mono">{a.alert_id}</div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: '12%',
      render: (a) => <Badge color="var(--status-info)">{a.category}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '10%',
      sortValue: (a) => a.status,
      render: (a) => (
        <Badge
          color={
            a.status === 'open'
              ? 'var(--status-critical)'
              : a.status === 'investigating'
                ? 'var(--status-degraded)'
                : a.status === 'contained'
                  ? 'var(--status-info)'
                  : 'var(--status-operational)'
          }
        >
          {a.status.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'time',
      header: 'Detected',
      width: '12%',
      mono: true,
      sortValue: (a) => a.detected_at,
      render: (a) => fmtRelative(a.detected_at),
    },
    {
      key: 'conf',
      header: 'Conf',
      width: '6%',
      mono: true,
      align: 'right',
      sortValue: (a) => a.confidence,
      render: (a) => `${a.confidence}%`,
    },
  ];
  return (
    <Card>
      <Table
        rows={alerts}
        columns={cols}
        rowKey={(a) => a.alert_id}
        onRowClick={(a) => navigate(`/alerts/${a.alert_id}`)}
        empty="No alerts in registry for this asset."
      />
    </Card>
  );
}

function DeviceTable({ devices }: { devices: OTDevice[] }) {
  const cols: Column<OTDevice>[] = [
    {
      key: 'name',
      header: 'Device',
      width: '24%',
      sortValue: (d) => d.name,
      render: (d) => (
        <div>
          <div>{d.name}</div>
          <div className="caption">
            {d.vendor} <span className="mono">{d.model}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      width: '12%',
      sortValue: (d) => d.device_type,
      render: (d) => <Badge color="var(--status-info)">{d.device_type}</Badge>,
    },
    {
      key: 'purdue',
      header: 'Purdue',
      width: '6%',
      mono: true,
      align: 'right',
      sortValue: (d) => d.purdue_level,
      render: (d) => `L${d.purdue_level}`,
    },
    {
      key: 'ip',
      header: 'IP',
      width: '12%',
      mono: true,
      sortValue: (d) => d.ip,
      render: (d) => d.ip,
    },
    {
      key: 'firmware',
      header: 'Firmware',
      width: '12%',
      mono: true,
      sortValue: (d) => d.firmware,
      render: (d) => d.firmware,
    },
    {
      key: 'protocols',
      header: 'Protocols',
      width: '20%',
      render: (d) => (
        <span className="mono caption">{d.protocols.join(', ')}</span>
      ),
    },
    {
      key: 'cves',
      header: 'CVEs',
      width: '8%',
      mono: true,
      align: 'right',
      sortValue: (d) => d.cves.length,
      render: (d) =>
        d.cves.length === 0 ? (
          <span className="caption">-</span>
        ) : (
          <Badge color="var(--status-degraded)">{d.cves.length}</Badge>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '6%',
      render: (d) => (
        <Badge
          color={
            d.status === 'online'
              ? 'var(--status-operational)'
              : d.status === 'degraded'
                ? 'var(--status-degraded)'
                : d.status === 'offline'
                  ? 'var(--status-critical)'
                  : 'var(--text-tertiary)'
          }
        >
          {d.status}
        </Badge>
      ),
    },
  ];
  return (
    <Card>
      <Table
        rows={devices}
        columns={cols}
        rowKey={(d) => d.device_id}
        density="compact"
        empty="No OT devices discovered for this asset."
      />
    </Card>
  );
}

function VulnerabilityTable({
  vulns,
}: {
  vulns: ReturnType<typeof useData.getState>['vulnerabilities'];
}) {
  if (vulns.length === 0)
    return (
      <Card>
        <div className="caption" style={{ padding: 8 }}>
          No CVEs match this asset's installed software/firmware.
        </div>
      </Card>
    );
  return (
    <Card>
      <ul className="iris-vuln-list">
        {vulns.map((v) => (
          <li key={v.cve_id}>
            <header>
              <span className="mono iris-vuln-id">{v.cve_id}</span>
              <Badge
                color={
                  v.cvss >= 9
                    ? 'var(--status-critical)'
                    : v.cvss >= 7
                      ? 'var(--status-degraded)'
                      : 'var(--status-info)'
                }
              >
                CVSS {v.cvss.toFixed(1)}
              </Badge>
              {v.exploited_in_wild && <Badge color="var(--status-critical)">In the wild</Badge>}
              {v.weaponised && <Badge color="var(--status-degraded)">Weaponised</Badge>}
              {v.patches_available && (
                <Badge color="var(--status-operational)">Patch available</Badge>
              )}
            </header>
            <div>{v.description}</div>
            <div className="caption">
              {v.vendor} {v.product} · versions {v.affected_versions.join(', ')}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function generateSensorSeries(condition: number) {
  const out: { t: string; output: number; temp: number }[] = [];
  for (let i = 0; i < 60; i++) {
    const ts = new Date(Date.now() - (59 - i) * 60_000);
    const hh = String(ts.getUTCHours()).padStart(2, '0');
    const mm = String(ts.getUTCMinutes()).padStart(2, '0');
    out.push({
      t: `${hh}:${mm}`,
      output: 70 + Math.sin(i / 6) * 8 + Math.random() * 2,
      temp: 60 + (100 - condition) * 0.2 + Math.sin(i / 8) * 4 + Math.random() * 1.5,
    });
  }
  return out;
}
